/* eslint-disable import/no-unresolved */
import { FC, useEffect, useMemo } from 'react';
import Datepicker from 'react-tailwindcss-datepicker';
import { useCheckoutContext } from '../../contexts/checkout.context';
import { 
  calculateBaseShipDate, 
  getCurrentDateInTimezone,
  calculateShipDateForMethod
} from '../../../../../helpers/dateHelpers';

/**
 * Convert YYYY-MM-DD string to local Date (avoids UTC parsing shift)
 * new Date("2026-01-21") parses as UTC midnight, which in PST shows as Jan 20.
 * new Date(2026, 0, 21) creates local midnight Jan 21 — no shift.
 */
function ymdToLocalDate(ymd: string): Date {
  if (!ymd || typeof ymd !== 'string') return new Date();
  const parts = ymd.split('T')[0].split('-');
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  return new Date(y, m - 1, d);
}

/**
 * Convert Date object to YYYY-MM-DD string in local timezone
 */
function dateToYmdLocal(date: Date | string | null | undefined): string {
  if (!date) return '';
  if (typeof date === 'string') {
    return date.split('T')[0];
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

interface ShippingDateProps {
  value: any;
  onChange: (value: any) => void;
  unavailabledDates: any[];
  shippingMethod?: string;
}

export const ShippingDate: FC<ShippingDateProps> = ({ 
  value, 
  onChange, 
  unavailabledDates = [],
  shippingMethod = ''
}) => {
  const inputId = 'shipping-date-input';
  const [{ 
    beyondTodayDate, 
    cutoffTime, 
    cutoffTimezone, 
    shipDateTable,
    transitDaysByMethod
  }] = useCheckoutContext();
  
  const minDate = useMemo(() => {
    const baseShipDate = calculateBaseShipDate(cutoffTimezone, cutoffTime, shipDateTable);
    const minShipDate = calculateShipDateForMethod(
      baseShipDate, 
      shippingMethod, 
      transitDaysByMethod
    );
    return ymdToLocalDate(minShipDate);
  }, [cutoffTimezone, cutoffTime, shipDateTable, shippingMethod, transitDaysByMethod]);
  
  const maxDate = useMemo(() => {
    const todayStr = getCurrentDateInTimezone(cutoffTimezone);
    const todayDate = ymdToLocalDate(todayStr);
    todayDate.setDate(todayDate.getDate() + beyondTodayDate);
    return todayDate;
  }, [cutoffTimezone, beyondTodayDate]);

  // Normalize value to local Date objects for the datepicker
  const datepickerValue = useMemo(() => {
    if (!value) return { startDate: null, endDate: null };
    
    let startStr = value.startDate;
    let endStr = value.endDate;
    
    // Handle if already Date objects
    if (startStr instanceof Date) startStr = dateToYmdLocal(startStr);
    if (endStr instanceof Date) endStr = dateToYmdLocal(endStr);
    
    return {
      startDate: startStr ? ymdToLocalDate(startStr) : null,
      endDate: endStr ? ymdToLocalDate(endStr) : null,
    };
  }, [value]);

  // Handle date selection and convert back to YYYY-MM-DD strings
  const handleChange = (val: any) => {
    if (!val) {
      onChange(val);
      return;
    }
    const normalizedValue = {
      startDate: dateToYmdLocal(val.startDate),
      endDate: dateToYmdLocal(val.endDate),
    };
    onChange(normalizedValue);
  };

  useEffect(() => {
    const inputElement = document.getElementById(inputId) as HTMLInputElement;
    if (inputElement) {
      const preventTyping = (e: KeyboardEvent) => e.preventDefault();

      inputElement.addEventListener('keydown', preventTyping);
      inputElement.addEventListener('input', preventTyping);

      return () => {
        inputElement.removeEventListener('keydown', preventTyping);
        inputElement.removeEventListener('input', preventTyping);
      };
    }
  }, []);

  return (
    <div className="flex flex-col space-y-2">
      <div>Selected Shipping Date</div>
      <Datepicker
        value={datepickerValue}
        minDate={minDate}
        maxDate={maxDate}
        disabledDates={unavailabledDates}
        displayFormat={'YYYY-MM-DD'}
        primaryColor="red"
        asSingle={true}
        useRange={false}
        readOnly
        onChange={handleChange}
        inputId={inputId}
      />
    </div>
  );
};
